import React, {
  useState,
  useEffect,
  useRef,
  MouseEventHandler,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "./SideBar";
import axios from "axios";
import { FaUserCircle, FaUser } from "react-icons/fa";
import "../styles/Topbar.css";
import { CiSearch } from "react-icons/ci";
import { RiNotification3Line } from "react-icons/ri";
import { CgLogOut } from "react-icons/cg";
import ConfirmationDialog from "./utils/ConfirmationDialog";
import { handleLogout } from "./API/ApiEnpoints";
import { FiDownload } from "react-icons/fi";
import openNotification from "./utils/CustomNotification";
import qs from "qs";
import { useTheme } from "../ThemeContext";
import { BsSun, BsMoon } from "react-icons/bs";


interface ConfirmationDialogProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCancel?: () => void; // Make onCancel optional
  message?: string;
  confirmText?: string;
  cancelText?: string;
  close?: boolean;
}

const TopBar: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const [showLogoutDialog, setShowLogoutDialog] = useState<boolean>(false);
  const sessionUid = localStorage.getItem("session_uid");
  const userUid = localStorage.getItem("user_uid");
  const orgUid = localStorage.getItem("org_uid");
  const release_type = localStorage.getItem("release_group") || "stable";
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileImageVersion, setProfileImageVersion] = useState(0);
  const [apkInfo, setApkInfo] = useState<{ apkfilename: string; release_type: string; version_code: string } | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [orgLogo, setOrgLogo] = useState<string | null>(null);
  const [collapseName, setCollapseName] = useState(false);
  const [hoveringProfile, setHoveringProfile] = useState(false);
  const { theme, handleThemeChange } = useTheme();


  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  useEffect(() => {
    // Check local storage for profile image
    const storedImage = localStorage.getItem("profileImage");
    if (storedImage) {
      setProfileImage(storedImage);
    }
    // Listen for storage events
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "profileImage") {
        setProfileImage(event.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setCollapseName(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const orgImageUid = localStorage.getItem("org_image_uid");
    const sessionUid = localStorage.getItem("session_uid");
    const userUid = localStorage.getItem("user_uid");

    if (orgImageUid && sessionUid && userUid) {
      axios.get(`${beAddress}/api/get-image-by-uid`, {
        headers: {
          "user-uid": userUid
        },
        params: {
          image_uid_list: [orgImageUid],
          is_icon_img: false,
          is_feedback_image: false
        },
        withCredentials: true,
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: 'repeat' })
      })
        .then((response) => {
          if (response.data.status === "Success") {
            const imageData = response.data.data?.[0]?.image;
            if (imageData) {
              setOrgLogo(`data:image/png;base64,${imageData}`);
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch org image:", err);
        });
    }
  }, []);


  useEffect(() => {
    const fetchApkInfo = async () => {
      try {
        const response = await axios.get(`${beAddress}/api/v1/get-apk-info`, {
          headers: {
            accept: 'application/json',
            'user-uid': userUid,
          },
          params: {
            org_uid: orgUid,
            latest: true,
            releasetype: release_type,
          },
          withCredentials: true,
        });

        if (response.status === 200 && response.data.status === 'Success') {
          const apk = response.data.data[0];
          setApkInfo(apk);
          localStorage.setItem("release_type", apk.release_group);
        }
        else {
          console.warn("APK Info fetch failed:", response.data.message);
        }
      } catch (err) {
        console.error("Error fetching APK info:", err);
      }
    };

    if (sessionUid && userUid && orgUid) {
      fetchApkInfo();
    }
  }, [beAddress, sessionUid, userUid, orgUid]);


  useEffect(() => {
    const handleProfileImageUpdate = (event: CustomEvent) => {
      setProfileImage(event.detail);
      setProfileImageVersion((prev) => prev + 1);
    };

    window.addEventListener(
      "profileImageUpdated",
      handleProfileImageUpdate as EventListener
    );

    return () => {
      window.removeEventListener(
        "profileImageUpdated",
        handleProfileImageUpdate as EventListener
      );
    };
  }, []);

  // const handleLogout = async () => {
  //   if (!sessionUid || !userUid) {
  //     console.error("Session UID or User UID is missing.");
  //     return;
  //   }
  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/api/logout`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           "session-uid": sessionUid,
  //           "user-uid": userUid,
  //         },
  //       }
  //     );

  //     if (response.ok) {
  //       // localStorage.removeItem("startDateTime");
  //       // localStorage.removeItem("endDateTime");
  //       localStorage.clear();
  //       window.location.href = "/";
  //     } else {
  //       console.error("Logout failed with status:", response.status);
  //     }
  //   } catch (error) {
  //     console.error("Logout error:", error);
  //   }
  // };

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${beAddress}/api/user-profile-images`, {
        headers: {
          "user-uid": userUid,
        },
        params: {
          user_list: userUid,
          icon_img: true,
        },
        withCredentials: true,
      });
      if (response.status === 200 && response.data.status === "Success") {
        const data = response.data.data[0];
        if (!data.default && data.image) {
          const updatedImage = `data:image/png;base64,${data.image}`;
          setProfileImage(updatedImage);
          localStorage.setItem("profileImage", updatedImage); // Store in local storage
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [sessionUid, userUid]);
  useEffect(() => {
    // Check local storage for profile image first
    const storedImage = localStorage.getItem("profileImage");
    if (storedImage) {
      setProfileImage(storedImage);
    } else {
      fetchData(); // Fetch from API if not found in local storage
    }
  }, [fetchData]);

  useEffect(() => {
    const handleProfileImageUpdate = () => {
      localStorage.removeItem("profileImage");
      fetchData();
    };

    window.addEventListener("profileImageUpdated", handleProfileImageUpdate);

    return () => {
      window.removeEventListener("profileImageUpdated", handleProfileImageUpdate);
    };
  }, [fetchData]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 576 && isOpen) {
        setIsOpen(false); // Close sidebar when the window width is greater than 576px
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const toggleDropdown = () => {
    setIsDropdownVisible(!isDropdownVisible);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsDropdownVisible(false);
  };
  const handleLogOutClick: MouseEventHandler<HTMLDivElement> = (event) => {
    // Show the confirmation dialog when logout is clicked
    setShowLogoutDialog(true);
  };
  const orgUidUppercase = orgUid ? orgUid.toUpperCase() : "";

  // const getApkInfo = async () => {
  //   try {
  //     const response = await axios.get(`${beAddress}/api/get-apk-info`, {
  //       headers: {
  //         accept: 'application/json',
  //         'session-uid': sessionUid,
  //         'user-uid': userUid,
  //       },
  //       params: {
  //         org_uid: orgUid,
  //         latest: true,
  //       },
  //     });

  //     if (response.status === 200 && response.data.status === 'Success') {
  //       return response.data.data; // contains apkfilename, release_type
  //     } else {
  //       throw new Error(response.data.message || 'Failed to fetch APK info');
  //     }
  //   } catch (error) {
  //     console.error('Error fetching APK info:', error);
  //     return null;
  //   }
  // };


  const handleApkDownload = async () => {
    if (!apkInfo || Object.keys(apkInfo).length === 0) {
      openNotification({ type: "info", message: "No latest APK available." });
      return;
    }

    if (!sessionUid || !userUid || !orgUid || isDownloading) return;

    setIsDownloading(true);

    const releaseType = release_type;
    const { apkfilename, version_code } = apkInfo;

    if (!version_code) {
      openNotification({ type: "error", message: "Missing version code in APK info." });
      setIsDownloading(false);
      return;
    }

    const params = `org_uid=${orgUid}&releasetype=${releaseType}&apkfilename=${apkfilename}&version_code=${version_code}`;

    setDownloadProgress(0);

    try {
      const response = await axios.get(`${beAddress}/api/v1/apk/apk-download?${params}`, {
        headers: {
          'user-uid': userUid,
        },
        withCredentials: true,
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setDownloadProgress(percent);
        }
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.android.package-archive',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = apkfilename + '.apk';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      openNotification({ type: "success", message: "App Download successful" });

      setTimeout(() => {
        setDownloadProgress(null);
        setIsDownloading(false);
      }, 2000);
    } catch (error) {
      openNotification({ type: "error", message: "App Download Error" });
      setDownloadProgress(null);
      setIsDownloading(false);
    }
  };


  //console.log("apkInfo:", apkInfo);
  //console.log("sessionUid:", sessionUid);
  //console.log("userUid:", userUid);
  //console.log("orgUid:", orgUid);
  //console.log("isDownloading:", isDownloading);
  return (
    <div>
      <div className="header-container">
        {/* Hamburger Button */}
        <button className="hamburger" onClick={toggleSidebar}>
          &#9776;
        </button>
        <div className={`sidebar ${isOpen ? "open" : ""}`}>
          <SideBar isOpen={isOpen} toggleSidebar={toggleSidebar} />
        </div>
        {/* Optional dark overlay */}
        <div className={`overlay ${isOpen ? "active" : ""}`} />
        {/* <span className="header-text">Metrics Made Easy</span> */}
        <div className="topbar-search-container">
          {orgLogo ? (
            <div className="org-logo-circle">
              <img src={orgLogo} alt="Org Logo" className="org-logo-image" />
            </div>

          ) : (
            <span className="header-text">{orgUidUppercase}</span>
          )}

          {/*<div className="topbar-search-box">
            <CiSearch className="topbar-search-icon" />
            <input
              type="text"
              placeholder="Search"
              // value={searchQuery}
              // onChange={(e) => handleSearch(e.target.value)}
              className="topbar-search-bar"
            />
          </div>*/}
        </div>
        <div className="left-section">
          {/* <img src={settings} alt="Settings" className="avatar" />*/}
          {/* <img src={Doughnut} alt="Doughnut" className="doughnut" />*/}
          {/* <span className="header-text">Metrics Made Smart</span> */}
        </div>

        <div className="right-section">
          <div
            className="apk-download-button"
            onClick={isDownloading ? undefined : handleApkDownload}
            title="Download Cofit App"
            style={{
              background: downloadProgress !== null
                ? `var(--sidebar-hover-color), #e0f7f6 ${downloadProgress}%)`
                : "var(--sidebar-hover-color)",
              color: isDownloading ? "var(--text-color)" : "var(--text-color)",
              cursor: isDownloading ? "not-allowed" : "pointer",
              pointerEvents: isDownloading ? "none" : "auto",
              transition: "background 0.3s ease",
            }}
          >
            {downloadProgress !== null
              ? "Downloading please wait..."
              : isDownloading
                ? "Preparing download..."
                : "Download the App"}
          </div>
          {/* Theme Switch */}
          <div
            className="theme-switch"
            onClick={() =>
              handleThemeChange(theme === "dark" ? "lightBlue" : "dark")
            }
          >
            <div className={`switch-track ${theme === "dark" ? "dark" : "light"}`}>
              <div className="switch-thumb">
                {theme === "dark" ? <BsMoon size={14} /> : <BsSun size={14} />}
              </div>
            </div>
          </div>





          <div className="right-section-container-main">
            {/* <div className="notification-name-div">
              <div
                onClick={() => navigate("/alerts")}
                style={{ cursor: "pointer" }}
              >
                <RiNotification3Line size={20} className="notification-icon" />
              </div>
            </div> */}

            <div
              className="user-profile-box"
              onMouseEnter={() => setHoveringProfile(true)}
              onMouseLeave={() => setHoveringProfile(false)}
              onClick={toggleDropdown}
              title="user"
            >
              {hoveringProfile && (
                <span className="username-expanded">{localStorage.getItem("user_uid")}</span>
              )}

              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="imageofuser"
                />
              ) : (
                <FaUserCircle className="imageofuser" />
              )}
            </div>


          </div>
          {isDropdownVisible && (
            <div ref={dropdownRef} className="dropdown-menu">
              <div
                className="dropdown-item"
                onClick={() => handleNavigation("/settings")}
              >
                <FaUser className="mnc-icon" /> Profile
              </div>
              <div className="dropdown-item" onClick={handleLogOutClick}>
                <CgLogOut className="mnc-icon" /> Logout
              </div>
              <div className="mnctopbar-line" />
              <div className="dropdown-item theme-selection">
                <div
                  className="theme-box"
                  style={{ backgroundColor: "#425eac" }}
                // onClick={() => handleThemeChange("theme1")}
                />
                <div
                  className="theme-box"
                  style={{ backgroundColor: "#9AC8CD" }}
                // onClick={() => handleThemeChange("theme2")}
                />
                <div
                  className="theme-box"
                  style={{ backgroundColor: "#fa6821" }}
                // onClick={() => handleThemeChange("theme3")}
                />
                <div
                  className="theme-box"
                  style={{ backgroundColor: "#c5c4c4" }}
                // onClick={() => handleThemeChange("theme4")}
                />
                <div
                  className="theme-box"
                  style={{ backgroundColor: "#333333" }}
                // onClick={() => handleThemeChange("theme5")}
                />
              </div>
            </div>
          )}
        </div>

      </div>
      <ConfirmationDialog
        show={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutDialog(false)}
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
      />

    </div>

  );


};

export default TopBar;
